/**
 * Số tiền của lượt gọi mô hình — port `so-tien.ts` native, thêm tham số `locale` (ADR-UI-0010: số đi theo ngôn ngữ).
 *
 * ── VÌ SAO KHÔNG DÙNG MẶC ĐỊNH CỦA `Intl` CHO SỐ NHỎ ─────────────────────
 * `style: 'currency'` lấy số chữ số thập phân theo tiền: USD là 2. Chi phí một lượt gọi mô hình thường nhỏ hơn một xu,
 * nên 0,0004 USD in ra **"0,00 US$"** — một số không trông y như số đo. Dưới 1 đơn vị tiền thì nới tới 6 chữ số.
 */

const NGUONG_SO_NHO = 1;
const SO_CHU_SO_NHO = 6;
const LOCALE_MAC_DINH = 'vi-VN';

const boNho = new Map<string, Intl.NumberFormat>();

function dinhDang(locale: string, donVi: string | null, soNho: boolean): Intl.NumberFormat {
  const khoa = `${locale}|${donVi ?? ''}|${soNho ? 'nho' : 'thuong'}`;
  const daCo = boNho.get(khoa);
  if (daCo) return daCo;
  const tuyChon: Intl.NumberFormatOptions = donVi
    ? { style: 'currency', currency: donVi }
    : { maximumFractionDigits: 2 };
  if (soNho) {
    tuyChon.minimumFractionDigits = 2;
    tuyChon.maximumFractionDigits = SO_CHU_SO_NHO;
  }
  let bo: Intl.NumberFormat;
  try {
    bo = new Intl.NumberFormat(locale, tuyChon);
  } catch {
    // Mã tiền không hợp lệ (`Intl` ném `RangeError`). Vẫn hiện được số, kèm mã thô.
    bo = new Intl.NumberFormat(
      locale,
      soNho ? { minimumFractionDigits: 2, maximumFractionDigits: SO_CHU_SO_NHO } : { maximumFractionDigits: 2 },
    );
  }
  boNho.set(khoa, bo);
  return bo;
}

/**
 * `numeric` dạng chuỗi → số, hoặc `null` khi không có số.
 *
 * audit-service trả cột tiền thành CHUỖI (`module/Hang.java`, như native). `Number()` chứ không `parseFloat()`:
 * `parseFloat('12abc')` là `12`, một trường đổi kiểu sẽ lẳng lặng thành con số trông đúng. Nhận cả `number` để một
 * ngày backend đổi bộ tuần tự hoá thì màn không trắng.
 *
 * Con số này chỉ dùng để HIỆN và để SO, không ghi trở lại: phép tính tiền nằm ở audit-service, trên `BigDecimal`.
 */
export function doTien(gia: string | number | null | undefined): number | null {
  if (gia === null || gia === undefined) return null;
  if (typeof gia === 'number') return Number.isFinite(gia) ? gia : null;
  const van = gia.trim();
  if (van === '') return null;
  const so = Number(van);
  return Number.isFinite(so) ? so : null;
}

/**
 * Chuỗi tiền để hiện, hoặc `null` khi không có số — nơi gọi hiện "—". `null` chứ không "0": không có số tiền và số
 * tiền bằng 0 là hai điều khác nhau.
 */
export function soTien(
  giaTri: number | string | null | undefined,
  donVi: string | null | undefined,
  locale: string = LOCALE_MAC_DINH,
): string | null {
  const so = doTien(giaTri);
  if (so === null) return null;
  const ma = donVi ?? null;
  const soNho = so !== 0 && Math.abs(so) < NGUONG_SO_NHO;
  const chuoi = dinhDang(locale, ma, soNho).format(so);
  // Mã tiền không hợp lệ thì rơi về số trần — ghép mã vào sau để người đọc vẫn biết đang xem tiền gì.
  if (ma !== null && !chuoi.includes(ma) && !/\p{Sc}/u.test(chuoi)) return `${chuoi} ${ma}`;
  return chuoi;
}
